'use client';

import type { QuantityDiscount } from '@eliadmin/core/src/api/products/types/product-types';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMemo } from 'react';
import { Paragraph } from '../../ui/paragraph';

const applyQuantityDiscount = (price: number | undefined, discount?: QuantityDiscount) => {
  if (price == null || !discount) return price;
  if (discount.discount_type === 'fixed') {
    return Math.max(0, price - discount.discount);
  }
  return Math.max(0, price - (price * discount.discount) / 100);
};

export function ProductDetailPrice() {
  const { t } = useTranslations();
  const settings = useGlobalStore(state => state.templateSettings);
  const product = useVariantsStore(state => state.product);
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const variants = useVariantsStore(state => state.product.variants);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const selectedServices = useVariantsStore(state => state.selectedServices);
  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);

  const me = useCustomerStore(state => state.me);

  const getServicesPrice = (vat = false) => {
    let price = 0;
    selectedServices.forEach(service => {
      price += vat
        ? parseFloat(service.price_with_vat.toString())
        : parseFloat(service.price.toString());
    });
    return price;
  };

  const desiredObject = variants?.length ? selectedVariant : product;
  const priceWithoutVatFirst = settings.product_detail?.price_without_vat_first?.enabled;
  const priceDisplay = getProductPriceDisplay(desiredObject ?? product, {
    isWholesaler: me?.customer.is_wholesaler,
    priceWithoutVatFirst,
  });
  const quantityDiscounts = useMemo(() => {
    const raw = (product as { quantity_discounts?: QuantityDiscount[] | QuantityDiscount })
      .quantity_discounts;
    if (!raw) return [];
    const discounts = Array.isArray(raw) ? raw : [raw];

    return discounts
      .filter(discount => Number(discount.min_quantity) > 0)
      .sort((a, b) => Number(a.min_quantity) - Number(b.min_quantity));
  }, [product]);

  const activeQuantityDiscount = useMemo(() => {
    const inRange = quantityDiscounts.filter(discount => {
      const min = Number(discount.min_quantity ?? 0);
      const max =
        discount.max_quantity == null ? Number.POSITIVE_INFINITY : Number(discount.max_quantity);
      return selectedQuantity >= min && selectedQuantity <= max;
    });

    if (inRange.length) {
      return inRange.sort((a, b) => Number(b.min_quantity) - Number(a.min_quantity))[0];
    }

    return quantityDiscounts
      .filter(discount => Number(discount.min_quantity) <= selectedQuantity)
      .sort((a, b) => Number(b.min_quantity) - Number(a.min_quantity))[0];
  }, [quantityDiscounts, selectedQuantity]);

  const hasActiveQuantityDiscount = !!activeQuantityDiscount;
  const hasProductDiscount = priceDisplay.showPreviousPrice;

  //   const servicesPrice = selectedServices.reduce(
  //     (acc, service) => acc + roundPrice(service.price_with_vat, currency),
  //     0,
  //   );
  const unit = settings.product_detail?.show_unit?.enabled ? ` / ${product.unit}` : '';
  const mainPriceUsesVat = !priceWithoutVatFirst;
  const mainPriceBeforeQuantityDiscount =
    priceDisplay.current == null
      ? undefined
      : priceDisplay.current + getServicesPrice(mainPriceUsesVat);
  const mainPriceAfterQuantityDiscount = applyQuantityDiscount(
    mainPriceBeforeQuantityDiscount,
    activeQuantityDiscount,
  );
  const mocPriceBeforeQuantityDiscount =
    priceDisplay.mocPrice == null
      ? undefined
      : priceDisplay.mocPrice + getServicesPrice(mainPriceUsesVat);
  const mocPriceAfterQuantityDiscount = applyQuantityDiscount(
    mocPriceBeforeQuantityDiscount,
    activeQuantityDiscount,
  );

  const mainPriceBeforeProductDiscount =
    priceDisplay.beforeDiscount == null
      ? undefined
      : priceDisplay.beforeDiscount + getServicesPrice(mainPriceUsesVat);
  const secondaryPriceWithoutVat =
    priceDisplay.secondary == null
      ? undefined
      : priceDisplay.secondary + getServicesPrice(priceDisplay.secondaryIncludesVat);
  const secondaryPriceWithoutVatAfterQuantityDiscount = applyQuantityDiscount(
    secondaryPriceWithoutVat,
    activeQuantityDiscount,
  );

  const renderErrorMessage = () => {
    if (selectedValues.length === product?.attributes?.length) {
      return (
        <span className={cn('text-2xl font-semibold text-destructive-foreground', '')}>
          {t('text495')}
        </span>
      );
    }
    if (product?.attributes?.length && selectedValues.length < product?.attributes?.length) {
      return (
        <span className={cn('text-2xl font-semibold text-destructive-foreground', '')}>
          {t('text496')}
        </span>
      );
    }
  };

  if ((!variants?.length || selectedVariant) && priceDisplay.priceOnRequest) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col items-start justify-start gap-1',
        'ea-product-detail-price',
      )}
    >
      {!selectedVariant && !!variants?.length ? (
        renderErrorMessage()
      ) : (
        <>
          <div
            className={cn(
              'flex flex-row items-center justify-start gap-4',
              'ea-product-detail-price__container',
            )}
          >
            <div>
              {priceDisplay.showMocVocPrices && mocPriceAfterQuantityDiscount != null ? (
                <div className={cn('text-sm', 'font-normal tracking-tight text-[#6B7280]')}>
                  {`${t('text1926')} ${formatPrice(
                    mocPriceAfterQuantityDiscount,
                    currency,
                    currencySettings,
                  )}${unit}`}
                </div>
              ) : null}
              <strong
                className={cn(
                  'h3',
                  'font-medium tracking-tight',
                  hasProductDiscount ? 'text-[#C31E1E]' : 'text-foreground',
                )}
              >{`${formatPrice(
                mainPriceAfterQuantityDiscount,
                currency,
                currencySettings,
              )}${unit}`}</strong>
            </div>
            {(hasProductDiscount || hasActiveQuantityDiscount) && (
              <span
                className={cn('text-[24px] leading-[28px]', 'ea-product-detail-price__with-vat')}
              >
                {hasActiveQuantityDiscount && !hasProductDiscount ? (
                  <del
                    className={cn(
                      'text-sm lg:text-base',
                      'font-normal tracking-tight text-[#6B7280]',
                    )}
                  >{`${formatPrice(
                    mainPriceBeforeQuantityDiscount,
                    currency,
                    currencySettings,
                  )}${unit}`}</del>
                ) : (
                  <>
                    <del
                      className={cn(
                        'text-sm lg:text-base',
                        'font-normal tracking-tight text-[#6B7280]',
                      )}
                    >{`${formatPrice(
                      mainPriceBeforeProductDiscount,
                      currency,
                      currencySettings,
                    )}${unit}`}</del>
                  </>
                )}
              </span>
            )}
          </div>
          <Paragraph
            className={cn('ea-product-detail-price__without-vat')}
            color="neutral"
            size="base"
          >
            {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
            {formatPrice(
              secondaryPriceWithoutVatAfterQuantityDiscount,
              currency,
              currencySettings,
            )}{' '}
            {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')} {unit}
          </Paragraph>
        </>
      )}
    </div>
  );
}
